// WebSocketService.js
class WebSocketService {
  constructor() {
    this.ws = null;
    this.listeners = new Map();
    this.connectionId = null;
    this.playerId = null;
    this.sessionId = null;
    this.connected = false;
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = 5;
    this.reconnectDelay = 1000;
    this.userProfile = null; // Store user profile from Welcome message
    this.isAuthenticated = false; // Track if authenticated via JWT

    // Heartbeat/Ping-Pong system
    this.pingInterval = null;
    this.lastPongTime = null;
    this.connectionHealthCallbacks = [];
  }

  connect(wsUrl) {
    return new Promise((resolve, reject) => {
      // If already connected, just resolve
      if (this.connected && this.ws && this.ws.readyState === WebSocket.OPEN) {
        console.log('WebSocket already connected');
        resolve();
        return;
      }

      // If connection in progress, wait for it
      if (this.ws && this.ws.readyState === WebSocket.CONNECTING) {
        console.log('WebSocket connection already in progress');
        // Wait for the existing connection to open or fail
        const checkConnection = setInterval(() => {
          if (this.ws.readyState === WebSocket.OPEN) {
            clearInterval(checkConnection);
            resolve();
          } else if (this.ws.readyState === WebSocket.CLOSED) {
            clearInterval(checkConnection);
            reject(new Error('Connection failed'));
          }
        }, 100);
        return;
      }

      try {
        this.ws = new WebSocket(wsUrl);

        this.ws.onopen = () => {
          console.log('WebSocket connected');
          this.connected = true;
          this.reconnectAttempts = 0;
          resolve();
        };

        this.ws.onmessage = (event) => {
          try {
            const message = JSON.parse(event.data);
            console.log('Received message:', message);
            this.handleMessage(message);
          } catch (error) {
            console.error('Error parsing message:', error);
          }
        };

        this.ws.onerror = (error) => {
          console.error('WebSocket error:', error);
          reject(error);
        };

        this.ws.onclose = () => {
          console.log('WebSocket closed');
          this.connected = false;
          this.handleDisconnect();
        };
      } catch (error) {
        reject(error);
      }
    });
  }

  handleMessage(message) {
    const { type } = message;

    // Special handling for Pong message - update last pong time
    if (type === 'Pong') {
      this.handlePong();
    }

    // Special handling for Identified message (replaces Welcome)
    if (type === 'Identified') {
      const { body } = message;
      if (body && body.accepted) {
        if (body.guest === false) {
          // Authenticated user - store profile data
          this.userProfile = {
            playerId: body.playerId,
            nickname: body.nickname,
            rating: body.rating,
            wins: body.wins,
            losses: body.losses
          };
          this.playerId = body.playerId;
          this.isAuthenticated = true;
          console.log('Authenticated user profile received:', this.userProfile);
        } else {
          // Guest user - playerId was already set when sending Identify
          this.isAuthenticated = false;
          console.log('Guest user identified:', body.playerId);
        }
      }
    }

    // Call all listeners registered for this message type
    const typeListeners = this.listeners.get(type) || [];
    typeListeners.forEach(callback => callback(message));

    // Call all listeners registered for all messages
    const allListeners = this.listeners.get('*') || [];
    allListeners.forEach(callback => callback(message));
  }

  handleDisconnect() {
    // Notify listeners about disconnect
    const disconnectListeners = this.listeners.get('Disconnect') || [];
    disconnectListeners.forEach(callback => callback({ type: 'Disconnect', sessionId: this.sessionId }));

    if (this.reconnectAttempts < this.maxReconnectAttempts && this.sessionId) {
      this.reconnectAttempts++;
      console.log(`Attempting to reconnect (${this.reconnectAttempts}/${this.maxReconnectAttempts})...`);

      setTimeout(() => {
        this.connect(this.ws.url).then(() => {
          // Re-identify and sync state after reconnection
          if (this.playerId) {
            this.identify(this.playerId, `Player ${this.playerId}`);
          }
          if (this.sessionId) {
            this.stateSync(0);
          }
        }).catch(error => {
          console.error('Reconnection failed:', error);
        });
      }, this.reconnectDelay * this.reconnectAttempts);
    }
  }

  on(messageType, callback) {
    if (!this.listeners.has(messageType)) {
      this.listeners.set(messageType, []);
    }
    this.listeners.get(messageType).push(callback);
  }

  off(messageType, callback) {
    if (this.listeners.has(messageType)) {
      const callbacks = this.listeners.get(messageType);
      const index = callbacks.indexOf(callback);
      if (index > -1) {
        callbacks.splice(index, 1);
      }
    }
  }

  send(message) {
    if (this.ws && this.connected) {
      // Remove undefined values from the message
      const cleanMessage = this.removeUndefined(message);
      this.ws.send(JSON.stringify(cleanMessage));
      console.log('Sent message:', cleanMessage);
    } else {
      console.error('WebSocket not connected');
    }
  }

  removeUndefined(obj) {
    if (obj === null || obj === undefined) {
      return obj;
    }

    if (Array.isArray(obj)) {
      return obj.map(item => this.removeUndefined(item)).filter(item => item !== undefined);
    }

    if (typeof obj === 'object') {
      const cleaned = {};
      for (const key in obj) {
        if (obj.hasOwnProperty(key) && obj[key] !== undefined) {
          cleaned[key] = this.removeUndefined(obj[key]);
        }
      }
      return cleaned;
    }

    return obj;
  }

  identify(playerId, playerName, authToken = null) {
    this.playerId = playerId;
    const body = {
      playerId,
      playerName
    };

    // Include token if provided (for authenticated users)
    if (authToken) {
      body.token = authToken;
    }

    const message = {
      v: 1,
      type: 'Identify',
      requestId: `identify-${Date.now()}`,
      body
    };
    this.send(message);
  }

  matchRequest(playerName, variant = 'Classic2', preferredColor = null, code = null, userId = null, isRanked = false) {
    const body = {
      ruleset: { variant },
      playerName
    };

    // Only include preferredColor if provided
    if (preferredColor) {
      body.preferredColor = preferredColor;
    }

    // Only include code if provided and non-empty
    if (code && code.trim() !== '') {
      body.code = code.trim();
    }

    // Include userId for authenticated users
    if (userId) {
      body.userId = userId;
    }

    // Include rated flag instead of isRanked
    if (isRanked) {
      body.rated = true;
    }

    const message = {
      v: 1,
      type: 'MatchRequest',
      requestId: `match-${Date.now()}`,
      body
    };
    this.send(message);
  }

  getUserProfile() {
    return this.userProfile;
  }

  getIsAuthenticated() {
    return this.isAuthenticated;
  }

  matchCancel(requestId) {
    const message = {
      v: 1,
      type: 'MatchCancel',
      body: { requestId }
    };
    this.send(message);
  }

  stateSync(lastReceivedMoveNo) {
    const message = {
      v: 1,
      type: 'StateSync',
      body: { lastReceivedMoveNo }
    };

    if (this.sessionId) {
      message.sessionId = this.sessionId;
    }

    this.send(message);
  }

  place(placement, turnNo, clientSeq) {
    const message = {
      v: 1,
      type: 'Place',
      requestId: `place-${Date.now()}`,
      body: {
        clientSeq,
        turnNo,
        placement
      }
    };

    if (this.sessionId) {
      message.sessionId = this.sessionId;
    }

    this.send(message);
  }

  pass(turnNo, clientSeq) {
    const message = {
      v: 1,
      type: 'Pass',
      requestId: `pass-${Date.now()}`,
      body: {
        clientSeq,
        turnNo
      }
    };

    if (this.sessionId) {
      message.sessionId = this.sessionId;
    }

    this.send(message);
  }

  resign(reason = 'player-resign') {
    const message = {
      v: 1,
      type: 'Resign',
      requestId: `resign-${Date.now()}`,
      body: { reason }
    };

    if (this.sessionId) {
      message.sessionId = this.sessionId;
    }

    this.send(message);
  }

  forget(authToken) {
    const message = {
      v: 1,
      type: 'Forget',
      requestId: `forget-${Date.now()}`,
      body: { token: authToken }
    };

    this.send(message);
  }

  ping(lastKnownMoveNo = 0) {
    const message = {
      v: 1,
      type: 'Ping',
      requestId: `ping-${Date.now()}`,
      body: { lastKnownMoveNo }
    };

    if (this.sessionId) {
      message.sessionId = this.sessionId;
    }

    this.send(message);
  }

  startHeartbeat(lastKnownMoveNo = 0) {
    // Stop any existing heartbeat
    this.stopHeartbeat();

    // Initialize last pong time
    this.lastPongTime = Date.now();

    // Send ping every 7 seconds
    this.pingInterval = setInterval(() => {
      if (this.connected && this.ws && this.ws.readyState === WebSocket.OPEN) {
        this.ping(lastKnownMoveNo);

        // Check connection health
        const timeSinceLastPong = Date.now() - this.lastPongTime;
        let healthStatus = 'healthy';

        if (timeSinceLastPong > 30000) {
          // 30+ seconds without pong - connection lost
          healthStatus = 'lost';
        } else if (timeSinceLastPong > 15000) {
          // 15-30 seconds without pong - unstable
          healthStatus = 'unstable';
        }

        // Notify listeners of connection health
        this.connectionHealthCallbacks.forEach(callback => {
          callback(healthStatus, timeSinceLastPong);
        });
      }
    }, 7000); // Send ping every 7 seconds
  }

  stopHeartbeat() {
    if (this.pingInterval) {
      clearInterval(this.pingInterval);
      this.pingInterval = null;
    }
  }

  onConnectionHealth(callback) {
    this.connectionHealthCallbacks.push(callback);
    // Return unsubscribe function
    return () => {
      this.connectionHealthCallbacks = this.connectionHealthCallbacks.filter(cb => cb !== callback);
    };
  }

  handlePong() {
    this.lastPongTime = Date.now();
  }

  disconnect() {
    // Stop heartbeat
    this.stopHeartbeat();

    if (this.ws) {
      this.ws.close();
      this.ws = null;
      this.connected = false;
      this.sessionId = null;
    }
  }
}

export default new WebSocketService();
